import React, { useState, useRef, useEffect } from 'react';
import { Edit, Check, X } from 'lucide-react';
import { Button } from './button';

interface EditableTitleProps {
  title: string;
  onSave?: (newTitle: string) => void;
  className?: string;
  inputClassName?: string;
  iconSize?: number;
  readOnly?: boolean;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onSave,
  className = '',
  inputClassName = '',
  iconSize = 16,
  readOnly = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (editValue.trim() !== title && onSave) {
      onSave(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleCancel}
          className={`bg-white border border-gray-300 rounded px-2 py-1 min-w-0 ${inputClassName}`}
        />
        <Button
          variant='ghost'
          size='xs'
          data-testid="save-button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="text-green-600 hover:text-green-700 p-1"
          type="button"
        >
          <Check size={iconSize} />
        </Button>
        <Button
          variant='ghost'
          size='xs'
          data-testid="cancel-button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleCancel();
          }}
          className="text-red-600 hover:text-red-700 p-1"
          type="button"
        >
          <X size={iconSize} />
        </Button>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className={className}>
        <span>{title}</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 group ${className}`}>
      <span>{title}</span>
      <Button
        variant='ghost'
        size='xs'
        aria-label="Edit title"
        onClick={() => setIsEditing(true)}
        className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 transition-opacity p-1"
        type="button"
      >
        <Edit size={iconSize} />
      </Button>
    </div>
  );
};

export default EditableTitle;